<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import dayjs from "dayjs";
import { formatNumber, formatNumberForScale } from "chartjs-graph";
import { useApiClient } from "@/api/client";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import useDialog from "@/ui/composables/useDialog";
import useToast from "@/ui/composables/useToast";
import { triggerDownload } from "@/Utils/triggerDownload";
import MainBreadcrumbs from "@/ui/components/header/MainBreadcrumbs.vue";
import PlanCreateDialog from "@/modules/base_price/components/PlanCreateDialog.vue";
import BasePricePlanMinimalDTO = Modules.BasePrice.Data.BasePricePlanMinimalDTO;
import PricingModel = Modules.BasePrice.Data.PricingModel;

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

/**
 * Netto or bruto. Kept in the query string so leaving a bruto editor lands back
 * on the bruto plans rather than on netto.
 */
const pricingModel = computed<PricingModel>({
	get: () => (route.query.pricing_model === "bruto" ? "bruto" : "netto"),
	set: (value) =>
		router.replace({ query: { ...route.query, pricing_model: value } }),
});

const isBruto = computed(() => pricingModel.value === "bruto");

const plans = ref<BasePricePlanMinimalDTO[]>([]);
const loading = ref(false);
const createDialogOpen = ref(false);

/**
 * Which list is on screen. A deleted plan can be restored but nothing else —
 * opening or exporting one would put a rate card somebody threw away back in
 * front of people — so the tab decides what a card can do, not just which
 * cards there are.
 */
const tab = ref<"active" | "trashed">("active");

const breadcrumbs = [
	{
		title: t("base_price.overview.title"),
		disabled: true,
		to: "/base-prices",
	},
];

const editPlan = (plan: BasePricePlanMinimalDTO) =>
	router.push(
		plan.pricing_model === "bruto"
			? `/base-prices/${plan.uuid}/bruto`
			: `/base-prices/${plan.uuid}/edit`
	);

/**
 * Two letters standing in for a face, as in the versions drawer. Falls back to
 * a single character rather than an empty circle, so a plan with no author
 * still lines up with the ones that have one.
 */
const initials = (name?: string | null): string => {
	const letters = (name ?? "")
		.split(" ")
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part[0].toUpperCase())
		.join("");

	return letters || "?";
};

const exporting = ref<string | null>(null);

/**
 * Publishes a plan as steering rules. Confirmed first because it replaces the
 * prices whatever quotes against that table is currently reading — not
 * destructively (the rules it supersedes keep their history), but visibly, and
 * not only for the person who clicked.
 */
const exportPlan = async (plan: BasePricePlanMinimalDTO) => {
	const confirmed = await useDialog().confirm({
		title: t("base_price.overview.export.confirm_title"),
		message: t("base_price.overview.export.confirm_message", {
			name: plan.name,
			year: plan.year,
		}),
		confirmButtonText: t("base_price.overview.export.confirm_button"),
	});

	if (!confirmed) return;

	exporting.value = plan.uuid;

	const response = await useApiClient("basePrice").exportSteeringRules(
		plan.uuid
	);

	exporting.value = null;

	if (response.isOk()) {
		useToast().success(t("base_price.overview.export.queued"));
		loadPlans();
	} else if (response.isErr()) {
		useToast().error(response.error.message);
	}
};

/**
 * Downloads the plan's remaining impressions as a CSV — a row per screen, a
 * column per day of its year. Handed to the browser as a plain download
 * rather than fetched here first, so the file streams straight to disk.
 */
const exportRemainingImpressions = (plan: BasePricePlanMinimalDTO) => {
	triggerDownload(
		useApiClient("basePrice").remainingImpressionsExportUrl(plan.uuid)
	);
	useToast().success(t("base_price.overview.remaining_impressions.started"));
};

const deleting = ref<string | null>(null);

/**
 * Throws a plan away. Confirmed because it is the one action here that takes a
 * plan off the list for good — the row survives, so versions saved from it
 * still make sense, but nothing in the app brings it back.
 */
const deletePlan = async (plan: BasePricePlanMinimalDTO) => {
	const confirmed = await useDialog().confirm({
		title: t("base_price.overview.delete.confirm_title"),
		message: t("base_price.overview.delete.confirm_message", {
			name: plan.name,
		}),
		confirmButtonText: t("base_price.overview.delete.confirm_button"),
		isDelete: true,
	});

	if (!confirmed) return;

	deleting.value = plan.uuid;

	const response = await useApiClient("basePrice").deletePlan(plan.uuid);

	deleting.value = null;

	if (response.isOk()) {
		useToast().success(t("base_price.overview.delete.done"));
		loadPlans();
	} else if (response.isErr()) {
		useToast().error(response.error.message);
	}
};

const restoring = ref<string | null>(null);

/**
 * Puts a deleted plan back as the live version of its chain. Not confirmed:
 * it adds something rather than taking it away, and it is refused outright if
 * the chain already has a live version.
 */
const restorePlan = async (plan: BasePricePlanMinimalDTO) => {
	restoring.value = plan.uuid;

	const response = await useApiClient("basePrice").restorePlan(plan.uuid);

	restoring.value = null;

	if (response.isOk()) {
		useToast().success(t("base_price.overview.restore.done"));
		loadPlans();
	} else if (response.isErr()) {
		useToast().error(response.error.message);
	}
};

const loadPlans = async () => {
	loading.value = true;

	const response = await useApiClient("basePrice").getPlans(
		tab.value === "trashed",
		pricingModel.value
	);
	if (response.isOk()) {
		plans.value = response.value;
	}

	loading.value = false;
};

// Emptied before the request rather than after it, so switching tabs never
// shows the other tab's plans under this tab's heading while it loads.
watch([tab, pricingModel], () => {
	plans.value = [];
	loadPlans();
});

onMounted(loadPlans);
</script>

<template>
	<div>
		<main-breadcrumbs :breadcrumbs="breadcrumbs" />

		<div class="d-flex align-center flex-wrap ga-4 mb-4">
			<v-tabs v-model="tab" density="compact">
				<v-tab value="active">{{ t("base_price.overview.tabs.active") }}</v-tab>
				<v-tab value="trashed">
					{{ t("base_price.overview.tabs.trashed") }}
				</v-tab>
				<v-divider class="my-0" />
			</v-tabs>

			<v-spacer />

			<v-btn-toggle
				v-model="pricingModel"
				mandatory
				density="compact"
				variant="outlined"
				color="primary"
				rounded="lg"
			>
				<v-btn value="netto" size="small">
					{{ t("base_price.overview.pricing_models.netto") }}
				</v-btn>
				<v-btn value="bruto" size="small">
					{{ t("base_price.overview.pricing_models.bruto") }}
				</v-btn>
			</v-btn-toggle>

			<v-btn
				v-if="tab === 'active'"
				prepend-icon="fa-light fa-plus"
				@click="createDialogOpen = true"
			>
				{{ t("base_price.overview.new_plan") }}
			</v-btn>
		</div>

		<div v-if="loading" class="d-flex justify-center pa-16">
			<v-progress-circular indeterminate color="primary" size="36" />
		</div>

		<v-card v-else-if="!plans.length" class="pa-16 text-center">
			<i
				class="fa-light fa-layer-group text-h4 text-medium-emphasis d-block mb-4"
			/>
			<div class="text-body-1 text-medium-emphasis">
				{{
					tab === "trashed"
						? t("base_price.overview.empty_trashed")
						: t("base_price.overview.empty")
				}}
			</div>
		</v-card>

		<div v-else class="plan-grid">
			<v-card
				v-for="plan in plans"
				:key="plan.uuid"
				class="plan-card"
				variant="flat"
			>
				<div class="pa-4 pb-3">
					<!--
						No status here: this list only ever shows open plans, so a chip
						saying so on every card would be decoration carrying no
						information.
					-->
					<div class="text-h6 font-weight-bold plan-card__name mb-1">
						{{ plan.name }}
					</div>

					<div
						class="d-flex align-center ga-2 text-caption text-medium-emphasis"
					>
						<span class="font-weight-medium"
							><v-chip density="compact" label>{{ plan.year }}</v-chip></span
						>
						<span class="plan-card__dot" />
						<span class="text-truncate" :title="plan.hierarchy_definition_name">
							{{ plan.hierarchy_definition_name || "—" }}
						</span>
					</div>
				</div>

				<!--
					The three figures that decide whether a plan is worth opening, in
					the same order everywhere: what it is worth, how it is carved up,
					and whether it ever went out.
				-->
				<!-- A bruto plan has no target, books or exports — only its history. -->
				<div v-if="isBruto" class="plan-card__stats plan-card__stats--single">
					<div class="plan-card__stat">
						<div class="plan-card__value">
							{{ plan.versions_in_chain ?? 1 }}
						</div>
						<div class="plan-card__label">
							{{ t("base_price.overview.headers.versions") }}
						</div>
					</div>
				</div>

				<div v-else class="plan-card__stats">
					<div class="plan-card__stat">
						<div
							class="plan-card__value"
							:title="formatNumber(plan.target, 'money')"
						>
							{{ formatNumberForScale("money", plan.target) }}
						</div>
						<div class="plan-card__label">
							{{ t("base_price.overview.headers.target") }}
						</div>
					</div>

					<div class="plan-card__stat">
						<div class="plan-card__value">{{ plan.books_count }}</div>
						<div class="plan-card__label">
							{{ t("base_price.overview.headers.books") }}
						</div>
					</div>

					<!--
						Counted across the chain, archived versions included: what is
						listed here is one version, and how much history sits behind it
						is the thing worth knowing.
					-->
					<div class="plan-card__stat">
						<div class="plan-card__value">
							{{ plan.versions_in_chain ?? 1 }}
						</div>
						<div class="plan-card__label">
							{{ t("base_price.overview.headers.versions") }}
						</div>
					</div>

					<!--
						Counted across the whole chain, not this version: this list hides
						archived versions, so a plan published three times and then saved
						again would otherwise read as never published.
					-->
					<div class="plan-card__stat">
						<div
							class="plan-card__value"
							:class="{
								'text-medium-emphasis': !plan.steering_rules_exports_in_chain,
							}"
						>
							{{ plan.steering_rules_exports_in_chain || "—" }}
						</div>
						<div class="plan-card__label">
							{{ t("base_price.overview.headers.exports") }}
						</div>
					</div>
				</div>

				<!-- Who and when, so the row below is nothing but actions. -->
				<div class="plan-card__meta">
					<v-chip size="small" variant="tonal">
						<v-avatar start color="primary" size="18">
							<span class="plan-card__initials">
								{{ initials(plan.created_by_user?.name) }}
							</span>
						</v-avatar>
						{{ plan.created_by_user?.name ?? "—" }}
					</v-chip>

					<span
						class="text-caption text-medium-emphasis"
						:title="t('base_price.overview.headers.created_at')"
					>
						{{
							plan.created_at
								? dayjs(plan.created_at).format("DD MMM YYYY · HH:mm")
								: "—"
						}}
					</span>
				</div>

				<!--
					An action bar of its own, separated by a rule. Sharing a line with
					the author and the timestamp left three controls wrapping below a
					name at narrow card widths, where nobody found them.
				-->
				<div class="plan-card__actions">
					<!--
						A trashed plan offers one thing and nothing else: opening or
						exporting it would put a rate card somebody threw away back in
						front of people.
					-->
					<template v-if="tab === 'trashed'">
						<v-btn
							size="small"
							variant="tonal"
							color="primary"
							prepend-icon="fa-light fa-arrow-rotate-left"
							:loading="restoring === plan.uuid"
							@click="restorePlan(plan)"
						>
							{{ t("base_price.overview.restore.action") }}
						</v-btn>
					</template>

					<template v-else>
						<!--
							Everything that acts on the plan rather than opening it, out of
							the way of the one thing most people came for.
						-->
						<v-menu location="bottom start">
							<template v-slot:activator="{ props: menu }">
								<v-btn
									v-bind="menu"
									icon
									size="28"
									variant="text"
									:loading="exporting === plan.uuid || deleting === plan.uuid"
									:aria-label="t('base_price.overview.menu.label')"
								>
									<v-icon size="18">fa-light fa-ellipsis-vertical</v-icon>
								</v-btn>
							</template>

							<v-list density="compact">
								<v-list-item @click="exportPlan(plan)">
									<template v-slot:prepend>
										<v-icon
											icon="fa-light fa-arrow-up-right-from-square"
											size="14"
											class="mr-2"
										/>
									</template>
									<v-list-item-title class="text-body-2">
										{{ t("base_price.overview.export.action") }}
									</v-list-item-title>
								</v-list-item>

								<v-list-item @click="exportRemainingImpressions(plan)">
									<template v-slot:prepend>
										<v-icon
											icon="fa-light fa-file-csv"
											size="14"
											class="mr-2"
										/>
									</template>
									<v-list-item-title class="text-body-2">
										{{ t("base_price.overview.remaining_impressions.action") }}
									</v-list-item-title>
								</v-list-item>

								<v-divider class="my-1" />

								<v-list-item @click="deletePlan(plan)">
									<template v-slot:prepend>
										<v-icon
											icon="fa-light fa-box-archive"
											size="14"
											color="error"
											class="mr-2"
										/>
									</template>
									<v-list-item-title class="text-body-2 text-error">
										{{ t("base_price.overview.menu.archive") }}
									</v-list-item-title>
								</v-list-item>
							</v-list>
						</v-menu>

						<v-spacer />

						<!-- Not hooked up for bruto yet: the server refuses it. -->
						<v-btn
							v-if="!isBruto"
							size="small"
							variant="tonal"
							color="primary"
							append-icon="fa-light fa-arrow-right"
							@click="editPlan(plan)"
						>
							{{ t("base_price.overview.open") }}
						</v-btn>
					</template>
				</div>
			</v-card>
		</div>

		<!-- Creating navigates straight into the new plan's editor, so there is
			nothing for this page to refresh. -->
		<plan-create-dialog
			v-model:visible="createDialogOpen"
			:pricing-model="pricingModel"
		/>
	</div>
</template>

<style scoped lang="scss">
.plan-grid {
	display: grid;
	// Cards find their own width: three across on a desktop, one on a phone,
	// without a breakpoint deciding it for them.
	grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
	gap: 16px;
}

.plan-card {
	display: flex;
	flex-direction: column;
	border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
	transition: box-shadow 0.15s ease;

	&:hover {
		box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
	}
}

.plan-card__name {
	line-height: 1.3;
	// Long names wrap rather than ellipsise: the name is the one thing on the
	// card nobody should have to hover to read.
	word-break: break-word;
}

.plan-card__dot {
	width: 3px;
	height: 3px;
	border-radius: 50%;
	background: rgba(var(--v-theme-on-surface), 0.35);
	flex: 0 0 auto;
}

.plan-card__stats {
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 8px;
	padding: 12px 16px;
	margin: 0 16px;
	border-radius: 8px;
	background: rgba(var(--v-theme-on-surface), 0.03);
}

.plan-card__stats--single {
	grid-template-columns: 1fr;
}

.plan-card__stat {
	min-width: 0;
}

.plan-card__value {
	font-size: 1.05rem;
	font-weight: 700;
	line-height: 1.3;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.plan-card__label {
	font-size: 11px;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	color: rgba(var(--v-theme-on-surface), 0.6);
}

.plan-card__meta {
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
	padding: 12px 16px 0;
	// Pushes everything below it down, so cards of unequal height still line
	// their actions up along the bottom of the row.
	margin-top: auto;
}

.plan-card__actions {
	display: flex;
	align-items: center;
	gap: 4px;
	flex-wrap: wrap;
	padding: 10px 12px 10px 8px;
	margin-top: 12px;
	border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.plan-card__initials {
	font-size: 9px;
	font-weight: 700;
	line-height: 1;
}
</style>
